/**
 * Base Service Class
 * Provides common CRUD operations for all entities
 */

import { createCondition, createSort, createPagination } from '../interfaces/IDatabaseAdapter.js'
import { SORT_DIRECTION } from '../interfaces/IDatabaseAdapter.js'

export class BaseService {
  collectionName: string
  db: any

  constructor(collectionName, databaseService = null) {
    this.collectionName = collectionName
    this.db = databaseService
  }

  /**
   * Set the database service instance
   * @param {Object} databaseService - Database service instance
   */
  setDatabaseService(databaseService) {
    this.db = databaseService
  }

  /**
   * Ensure database service is available
   * @private
   */
  async _ensureDatabaseService() {
    if (!this.db) {
      // Try to get the database service from the global instance
      const { db } = await import('../index.js')
      if (db && db.isInitialized()) {
        this.db = db
      } else {
        throw new Error('Database service not initialized')
      }
    }
  }

  /**
   * Get all documents
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of documents
   */
  async getAll(options = {}) {
    await this._ensureDatabaseService()
    return await this.db.list(this.collectionName, options)
  }

  /**
   * Get a document by ID
   * @param {string} id - Document ID
   * @returns {Promise<Object|null>} Document or null if not found
   */
  async getById(id) {
    await this._ensureDatabaseService()
    return await this.db.get(this.collectionName, id)
  }

  /**
   * Create a new document
   * @param {Object} data - Document data
   * @param {string} id - Optional custom ID
   * @returns {Promise<Object>} Created document
   */
  async create(data, id = null) {
    await this._ensureDatabaseService()
    return await this.db.create(this.collectionName, data, id)
  }

  /**
   * Update a document
   * @param {string} id - Document ID
   * @param {Object} data - Update data
   * @param {boolean} merge - Whether to merge with existing data
   * @returns {Promise<Object>} Updated document
   */
  async update(id, data, merge = true) {
    await this._ensureDatabaseService()
    return await this.db.update(this.collectionName, id, data, merge)
  }

  /**
   * Create or update a document
   * @param {string} id - Document ID
   * @param {Object} data - Document data
   * @returns {Promise<Object>} Upserted document
   */
  async upsert(id, data) {
    await this._ensureDatabaseService()
    return await this.db.upsert(this.collectionName, id, data)
  }

  /**
   * Delete a document
   * @param {string} id - Document ID
   * @returns {Promise<boolean>} Deletion success status
   */
  async delete(id) {
    await this._ensureDatabaseService()
    return await this.db.delete(this.collectionName, id)
  }

  /**
   * Find documents by field value
   * @param {string} field - Field name
   * @param {*} value - Field value
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of matching documents
   */
  async findBy(field, value, options: any = {}) {
    await this._ensureDatabaseService()
    const conditions = [createCondition(field, '==', value)]
    
    // Add additional filters if provided
    if (options.additionalFilters) {
      conditions.push(...options.additionalFilters.map(filter => 
        createCondition(filter[0], filter[1], filter[2])
      ))
    }
    
    return await this.db.query(this.collectionName, conditions, options)
  }

  /**
   * Find documents by multiple conditions
   * @param {Array} conditions - Array of condition objects
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of matching documents
   */
  async findWhere(conditions, options = {}) {
    await this._ensureDatabaseService()
    return await this.db.query(this.collectionName, conditions, options)
  }

  /**
   * Batch query with "in" operator using POST body (for large arrays)
   * @param {string} field - Field name
   * @param {Array} values - Array of values to match
   * @returns {Promise<Array>} Array of matching documents
   */
  async findByIn(field, values) {
    if (!values || values.length === 0) {
      return []
    }
    
    await this._ensureDatabaseService()
    
    // Use HTTP adapter's batch-query endpoint to handle large arrays
    if (this.db.adapter && this.db.adapter.baseUrl) {
      try {
        const url = `${this.db.adapter.baseUrl}/batch-query`
        
        // Get auth token from localStorage
        const authToken = localStorage.getItem('access_token')
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ 
            collection: this.collectionName,
            field,
            operator: 'in',
            values
          })
        })
        
        if (!response.ok) {
          throw new Error(`Failed to batch query: ${response.statusText}`)
        }
        
        return await response.json()
      } catch (error) {
        // Fallback to individual queries if batch fails
      }
    } else {
      // Try to get baseUrl from db service
      let baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://dev.services.leadconnectorhq.com/hltestmanager'
      if (this.db && this.db.apiBaseUrl) {
        baseUrl = this.db.apiBaseUrl
      }
      
      try {
        // Get auth token from localStorage
        const authToken = localStorage.getItem('access_token')
        
        const response = await fetch(`${baseUrl}/batch-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ 
            collection: this.collectionName,
            field,
            operator: 'in',
            values
          })
        })
        
        if (!response.ok) {
          throw new Error(`Failed to batch query: ${response.statusText}`)
        }
        
        return await response.json()
      } catch (error) {
        // Fallback to individual queries if direct call also fails
      }
    }
    
    // Fallback: use Promise.all for individual queries
    const results = await Promise.all(
      values.map(value => this.findBy(field, value))
    )
    return results.flat()
  }

  /**
   * Search documents
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching documents
   */
  async search(searchTerm, options = {}) {
    await this._ensureDatabaseService()
    return await this.db.searchDocuments(this.collectionName, searchTerm, options)
  }

  /**
   * Get paginated results
   * @param {number} page - Page number (1-based)
   * @param {number} limit - Items per page
   * @param {Object} filters - Filter conditions
   * @param {Array} orderBy - Sort conditions
   * @returns {Promise<Object>} Paginated results
   */
  async getPaginated(page = 1, limit = 20, filters = [], orderBy = []) {
    await this._ensureDatabaseService()

    const pagination = createPagination(page, limit)
    const options = {
      ...pagination,
      filters,
      orderBy
    }

    const data = await this.db.query(this.collectionName, filters, options)
    const total = await this.getCount(filters)

    return {
      data,
      pagination: {
        page,
        limit,
        total,
        totalPages: Math.ceil(total / limit),
        hasNext: page < Math.ceil(total / limit),
        hasPrev: page > 1
      }
    }
  }

  /**
   * Get count of documents matching conditions
   * @param {Array} filters - Filter conditions
   * @returns {Promise<number>} Count of matching documents
   */
  async getCount(filters = []) {
    await this._ensureDatabaseService()
    const documents = await this.db.query(this.collectionName, filters)
    return documents.length
  }

  /**
   * Batch operations
   * @param {Array} operations - Array of operation objects
   * @returns {Promise<Array>} Results of batch operations
   */
  async batch(operations: any[]) {
    await this._ensureDatabaseService()
    return await this.db.batch(operations)
  }

  /**
   * Validate document data against schema
   * @param {Object} data - Document data
   * @param {Object} schema - Schema definition
   * @returns {Object} Validation result
   */
  validateData(data: any, schema: any) {
    const errors = []
    const validated = {}

    for (const [field, definition] of Object.entries(schema)) {
      const value = data[field]

      // Check required fields
      if ((definition as any).required && (value === undefined || value === null)) {
        errors.push(`${field} is required`)
        continue
      }

      // Skip validation if value is not provided and not required
      if (value === undefined || value === null) {
        if ((definition as any).default !== undefined) {
          validated[field] = (definition as any).default
        }
        continue
      }

      // Type validation
      if ((definition as any).type && !this.validateType(value, (definition as any).type)) {
        errors.push(`${field} must be of type ${(definition as any).type}`)
        continue
      }

      // Enum validation
      if ((definition as any).enum && !((definition as any).enum as any[]).includes(value)) {
        errors.push(`${field} must be one of: ${(definition as any).enum.join(', ')}`)
        continue
      }

      // Include the field in validated data
      validated[field] = value
    }

    return {
      isValid: errors.length === 0,
      errors,
      data: validated
    }
  }

  /**
   * Validate data type
   * @param {*} value - Value to validate
   * @param {string} type - Expected type
   * @returns {boolean} Whether value matches type
   */
  validateType(value: any, type: string) {
    switch (type) {
      case 'string':
        return typeof value === 'string'
      case 'number':
        return typeof value === 'number' && !isNaN(value)
      case 'boolean':
        return typeof value === 'boolean'
      case 'array':
        return Array.isArray(value)
      case 'object':
        return typeof value === 'object' && value !== null && !Array.isArray(value)
      case 'datetime':
        return value instanceof Date || !isNaN(Date.parse(value))
      default:
        return true
    }
  }

  /**
   * Create sort condition
   * @param {string} field - Field name
   * @param {string} direction - Sort direction
   * @returns {Object} Sort condition
   */
  _orderBy(field: string, direction = SORT_DIRECTION.ASC) {
    return createSort(field, direction)
  }

  /**
   * Create filter condition
   * @param {string} field - Field name
   * @param {string} operator - Query operator
   * @param {*} value - Value to compare
   * @returns {Object} Filter condition
   */
  _where(field: string, operator: string, value: any) {
    return createCondition(field, operator, value)
  }
}